import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import "./styles.css";
import { SiteNav } from "./components/SiteNav";
import { SiteFooter } from "./components/SiteFooter";
import { HomeBackdrop } from "./components/HomeBackdrop";
import { Route as IndexRoute } from "./routes/index";
import { Route as V2Route } from "./routes/v2";
import { Route as ClassesRoute } from "./routes/classes";
import { Route as MainePanProductionRoute } from "./routes/maine-pan-production";
import { Route as AtlanticClarionRoute } from "./routes/atlantic-clarion";
import { Route as PanCoalitionRoute } from "./routes/pan-coalition";
import { Route as PlanetPanRoute } from "./routes/planet-pan";
import { Route as CalendarRoute } from "./routes/calendar";
import { Route as MondayNightsRoute } from "./routes/monday-nights";
import { Route as PanFestivalRoute } from "./routes/pan-festival";
import { Route as LastNightRoute } from "./routes/last-night";
import { Route as RecordingsRoute } from "./routes/recordings";
import { Route as SheetMusicRoute } from "./routes/sheet-music";
import { Route as HistoryRoute } from "./routes/history";
import { Route as NigelRoute } from "./routes/nigel";
import { Route as ContactRoute } from "./routes/contact";
import { Route as LearnToPlayRoute } from "./routes/learn-to-play";
import { Route as LogosRoute } from "./routes/logos";
import { Route as VisualizerIndexRoute } from "./routes/visualizer.index";
import { Route as VisualizerTenorRoute } from "./routes/visualizer.tenor";
import { Route as VisualizerCelloRoute } from "./routes/visualizer.cello";
import { Route as VisualizerBassRoute } from "./routes/visualizer.bass";

const FULL_ROUTES = new Set(["/", "/learn-to-play"]);

const ROUTES = [
  { path: "/", route: IndexRoute },
  { path: "/v12", route: IndexRoute },
  { path: "/v2", route: V2Route },
  { path: "/classes", route: ClassesRoute },
  { path: "/maine-pan-production", route: MainePanProductionRoute },
  { path: "/atlantic-clarion", route: AtlanticClarionRoute },
  { path: "/pan-coalition", route: PanCoalitionRoute },
  { path: "/planet-pan", route: PlanetPanRoute },
  { path: "/calendar", route: CalendarRoute },
  { path: "/monday-nights", route: MondayNightsRoute },
  { path: "/pan-festival", route: PanFestivalRoute },
  { path: "/last-night", route: LastNightRoute },
  { path: "/recordings", route: RecordingsRoute },
  { path: "/sheet-music", route: SheetMusicRoute },
  { path: "/history", route: HistoryRoute },
  { path: "/nigel", route: NigelRoute },
  { path: "/contact", route: ContactRoute },
  { path: "/learn-to-play", route: LearnToPlayRoute },
  { path: "/logos", route: LogosRoute },
  { path: "/visualizer", route: VisualizerIndexRoute },
  { path: "/visualizer/", route: VisualizerIndexRoute },
  { path: "/visualizer/tenor", route: VisualizerTenorRoute },
  { path: "/visualizer/cello", route: VisualizerCelloRoute },
  { path: "/visualizer/bass", route: VisualizerBassRoute },
];

function applyHead(route) {
  const head = route?.options?.head;
  if (!head) return;
  const result = typeof head === "function" ? head() : head;
  const title = result?.meta?.find((item) => item.title)?.title;
  const description = result?.meta?.find((item) => item.name === "description")?.content;

  if (title) {
    document.title = title;
  }

  if (description) {
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", description);
  }
}

function RouteRenderer({ route }) {
  const Component = route.options.component;

  useEffect(() => {
    applyHead(route);
  }, [route]);

  return Component ? <Component /> : null;
}

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you&apos;re looking for doesn&apos;t exist or has been moved.
        </p>
        <a
          href="/"
          className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Go home
        </a>
      </div>
    </div>
  );
}

function Shell() {
  const location = useLocation();
  const isFull = FULL_ROUTES.has(location.pathname);

  return (
    <>
      {!isFull && <HomeBackdrop />}
      <SiteNav />
      <Routes>
        {ROUTES.map(({ path, route }) => (
          <Route key={path} path={path} element={<RouteRenderer route={route} />} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <SiteFooter />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
